/**
 * Cliente da API da UCC.
 *
 * Fino de propósito: uma função que fala HTTP e um erro tipado. A cache, o
 * retry e o estado de carregamento ficam por conta do TanStack Query, que já
 * resolve isso melhor do que qualquer camada caseira.
 */

const BASE = import.meta.env['VITE_API_URL'] ?? '/api';

/**
 * Intervalo das telas que só mostram resultado (classificação e chave): os
 * dados se atualizam sozinhos durante a rodada, sem recarregar a página. O
 * TanStack Query pausa com a aba em segundo plano e mantém os dados na tela
 * enquanto busca, então não há piscar nem "carregando".
 */
export const ATUALIZACAO_AO_VIVO = 30_000;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      // FormData é a exceção: o navegador precisa montar o Content-Type
      // sozinho para incluir o `boundary`. Definir manualmente aqui faria o
      // upload chegar ilegível do outro lado.
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: { code?: string; message?: string; detail?: unknown } }
      | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? 'Não foi possível concluir a operação.',
      body?.error?.detail ?? null,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/* ------------------------------ tipos ------------------------------ */

export interface TournamentSummary {
  /** Campeão declarado pela organização, sem placar de final (dados perdidos). */
  campeaoDeclaradoId?: string | null;
  campeaoDeclaradoMotivo?: string | null;
  id: string;
  slug: string;
  name: string;
  edition: string | null;
  game: 'fc26' | 'fc27';
  capacity: number;
  spotsTaken: number;
  spotsLeft: number;
  entryFeeCents: number;
  prizePoolCents: number;
  startsAt: number | null;
  /** Vem na resposta da lista; usado para ordenar edições que não têm data. */
  createdAt?: number;
  status:
    | 'draft'
    | 'registration'
    | 'ready'
    | 'group_stage'
    | 'knockout'
    | 'finished'
    | 'cancelled';
  coverUrl: string | null;
  /**
   * Quem levantou a taça. Só vem nas edições encerradas, e é derivado da
   * final — não há coluna de campeão, e derivar é o que garante que o card
   * nunca discorde da chave.
   */
  champion?: {
    id: string;
    name: string;
    shortName: string;
    slug: string;
    crestUrl: string | null;
  } | null;
  runnerUp?: {
    id: string;
    name: string;
    shortName: string;
    slug: string;
    crestUrl: string | null;
  } | null;
  finalScore?: {
    champion: number | null;
    runnerUp: number | null;
    /** Presentes só quando a decisão foi para os pênaltis. */
    championPenalties?: number | null;
    runnerUpPenalties?: number | null;
  };
}

export interface StandingRowDto {
  position: number;
  teamId: string;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  cards: number;
  points: number;
  qualified: boolean;
  decidedBy: string | null;
  needsManualTiebreak: boolean;
}

/** Agente do coletor. O token só existe no momento da emissão — aqui nunca vem. */
export interface AgentDto {
  id: string;
  name: string;
  lastSeenAt: number | null;
  lastIp: string | null;
  revokedAt: number | null;
  createdAt: number;
}

export interface EaClubSearchResult {
  clubId: string;
  name: string;
  currentDivision: string;
  wins: string;
  losses: string;
  ties: string;
  gamesPlayed: string;
  goals: string;
}

export interface TeamDto {
  /**
   * Títulos e vice-campeonatos, contados a partir das finais decididas.
   *
   * Opcionais porque só a listagem de times os calcula — nas outras rotas o
   * time vem sem eles, e a tela precisa tratar isso como zero e não como
   * ausência de informação.
   */
  titles?: number;
  runnerUps?: number;
  id: string;
  slug: string;
  name: string;
  shortName: string;
  crestUrl: string | null;
  platform: 'ps5' | 'xbox-series' | 'pc';
  eaClubId: string | null;
  /** Carimbo de "o capitão confirmou que é este o clube". */
  eaVerifiedAt?: number | null;
  /** Quem capitaneia. O servidor já mandava; o tipo é que não dizia. */
  captainId?: string | null;
  primaryColor?: string | null;
  discordInvite?: string | null;
}

export type MatchStage = 'group' | 'round_of_16' | 'quarter' | 'semi' | 'third_place' | 'final';

export type MatchStatus =
  | 'scheduled'
  | 'live'
  | 'awaiting_report'
  | 'disputed'
  | 'finished'
  | 'walkover'
  | 'cancelled';

export interface MatchDto {
  id: string;
  tournamentId: string;
  stage: MatchStage;
  bracketKey: string | null;
  groupId: string | null;
  round: number | null;
  homeTeamId: string | null;
  awayTeamId: string | null;
  roomHostTeamId: string | null;
  scheduledAt: number | null;
  homeGoals: number | null;
  awayGoals: number | null;
  homeCards: number;
  awayCards: number;
  /** Divisão por cor; nulo quando só se conhece o total (jogos vindos da EA). */
  homeYellowCards: number | null;
  homeRedCards: number | null;
  awayYellowCards: number | null;
  awayRedCards: number | null;
  homePenalties: number | null;
  awayPenalties: number | null;
  status: MatchStatus;
  resultSource: 'manual' | 'ea_api' | 'walkover' | 'abandon' | 'sumula' | null;
  eaMatchId: string | null;
  eaMatchConfidence: number | null;
  eaSyncedAt: number | null;
  verifiedAt: number | null;
  notes: string | null;
}

export interface GroupDto {
  id: string;
  tournamentId: string;
  key: string;
}

export interface BracketSlotDto {
  key: string;
  stage: MatchStage;
  homeTeamId: string | null;
  awayTeamId: string | null;
  match: MatchDto | null;
}

export interface PlayerDto {
  id: string;
  teamId: string;
  displayName: string;
  eaPlayerName: string | null;
  gamertag: string | null;
  position: string | null;
  shirtNumber: number | null;
  isCaptain: boolean;
}

export interface MatchPlayerStatDto {
  id: string;
  matchId: string;
  teamId: string;
  playerId: string | null;
  eaPlayerName: string | null;
  goals: number;
  assists: number;
  shots: number | null;
  passesMade: number | null;
  passAttempts: number | null;
  tacklesMade: number | null;
  tackleAttempts: number | null;
  saves: number | null;
  /** Já convertido pela API: 7.35, não 735. */
  rating: number | null;
  redCards: number;
  yellowCards: number;
  manOfTheMatch: boolean;
  position: string | null;
  player: PlayerDto | null;
}

export interface LeaderRowDto {
  position: number;
  playerId: string;
  /**
   * Apelido do jogador, resolvido pelo servidor.
   *
   * Opcional porque uma resposta em cache de antes desta mudança não traz o
   * campo — e aí a tela cai no nome cru em vez de mostrar `undefined`.
   */
  name?: string;
  /** Tag do Virtual Pro, quando difere do nome cadastrado. */
  proName?: string | null;
  teamId: string;
  matches: number;
  goals: number;
  assists: number;
  contributions: number;
  averageRating: number | null;
  motm: number;
}

export interface TournamentDetail extends TournamentSummary {
  drawSeed: string | null;
  drawnAt: number | null;
  groupCount: number;
  qualifiersPerGroup: number;
  server: string | null;
  rulesUrl: string | null;
  prizeBreakdown: Array<{ label: string; amountCents: number }> | null;
  registrationClosesAt: number | null;
  /**
   * Dados de recebimento. Opcionais no tipo de propósito: as rotas públicas
   * removem estes campos da resposta, então quem lê `/tournaments/:slug` não
   * pode contar com eles. Só as rotas do painel devolvem preenchido.
   */
  pixKey?: string | null;
  pixName?: string | null;
  pixCity?: string | null;
}

export const queries = {
  tournaments: (status?: string) => ({
    queryKey: ['tournaments', status ?? 'all'] as const,
    queryFn: () =>
      api<{ tournaments: TournamentSummary[] }>(
        `/tournaments${status ? `?status=${encodeURIComponent(status)}` : ''}`,
      ),
  }),
  tournament: (slug: string) => ({
    queryKey: ['tournament', slug] as const,
    queryFn: () =>
      api<{
        tournament: TournamentDetail;
        teams: Array<{ registrationId: string; status: string; team: TeamDto }>;
        spotsTaken: number;
      }>(`/tournaments/${slug}`),
  }),
  standings: (slug: string) => ({
    queryKey: ['standings', slug] as const,
    queryFn: () =>
      api<{ standings: Record<string, StandingRowDto[]>; teams: Record<string, TeamDto> }>(
        `/tournaments/${slug}/standings`,
      ),
  }),
  bracket: (slug: string) => ({
    queryKey: ['bracket', slug] as const,
    queryFn: () =>
      api<{ bracket: BracketSlotDto[]; teams: Record<string, TeamDto> }>(
        `/tournaments/${slug}/bracket`,
      ),
  }),
  matches: (slug: string) => ({
    queryKey: ['matches', slug] as const,
    queryFn: () =>
      api<{ matches: Array<{ match: MatchDto; group: GroupDto | null }> }>(
        `/tournaments/${slug}/matches`,
      ),
  }),
  match: (id: string) => ({
    queryKey: ['match', id] as const,
    queryFn: () =>
      api<{
        match: MatchDto;
        teams: Record<string, TeamDto>;
        playerStats: MatchPlayerStatDto[];
      }>(`/matches/${id}`),
  }),
  leaders: (slug: string) => ({
    queryKey: ['leaders', slug] as const,
    queryFn: () =>
      api<{ scorers: LeaderRowDto[]; assists: LeaderRowDto[] }>(`/tournaments/${slug}/leaders`),
  }),
  team: (slug: string) => ({
    queryKey: ['team', slug] as const,
    queryFn: () => api<{ team: TeamDto; players: PlayerDto[] }>(`/teams/${slug}`),
  }),
  teams: () => ({
    queryKey: ['teams'] as const,
    queryFn: () => api<{ teams: TeamDto[] }>('/teams'),
  }),
};

/* ------------------------------ provas ------------------------------ */

export interface EvidenceDto {
  id: string;
  matchId: string;
  teamId: string | null;
  kind: 'screenshot' | 'video' | 'other';
  caption: string | null;
  createdAt: number;
  /** Servido pela nossa rota, nunca direto do bucket. */
  url: string | null;
  externalUrl: string | null;
}

export interface MatchReportDto {
  id: string;
  matchId: string;
  teamId: string;
  homeGoals: number;
  awayGoals: number;
  scorers: Array<{ eaPlayerName: string; goals: number; assists: number }> | null;
  createdAt: number;
}

export const evidenceApi = {
  list: (matchId: string) => api<{ evidence: EvidenceDto[] }>(`/matches/${matchId}/evidence`),
  addLink: (matchId: string, externalUrl: string, caption?: string) =>
    api<{ evidence: EvidenceDto }>(`/matches/${matchId}/evidence`, {
      method: 'POST',
      body: JSON.stringify({ externalUrl, caption }),
    }),
  remove: (id: string) => api<{ ok: true }>(`/evidence/${id}`, { method: 'DELETE' }),
  reports: (matchId: string) =>
    api<{ reports: MatchReportDto[]; agree: boolean | null }>(`/matches/${matchId}/reports`),
  report: (matchId: string, homeGoals: number, awayGoals: number) =>
    api<{ outcome: 'awaiting_other' | 'agreed' | 'diverged'; reports: MatchReportDto[] }>(
      `/matches/${matchId}/report`,
      { method: 'POST', body: JSON.stringify({ homeGoals, awayGoals }) },
    ),
};

/* ------------------------------ tickets ------------------------------ */

export type TicketCategory =
  | 'disconnect'
  | 'cheating'
  | 'conduct'
  | 'no_show'
  | 'score'
  | 'other';

export type TicketStatus = 'open' | 'in_review' | 'resolved' | 'rejected';

export interface TicketDto {
  id: string;
  tournamentId: string | null;
  matchId: string | null;
  teamId: string | null;
  openedBy: string | null;
  category: TicketCategory;
  subject: string;
  body: string;
  status: TicketStatus;
  resolution: string | null;
  resolvedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface TicketContext {
  tournaments: Record<string, { id: string; name: string; slug: string }>;
  teams: Record<string, { id: string; name: string; shortName: string; slug: string }>;
  matches: Record<
    string,
    {
      id: string;
      stage: MatchStage;
      round: number | null;
      scheduledAt: number | null;
      homeTeamId: string | null;
      awayTeamId: string | null;
      status?: MatchStatus;
      homeGoals?: number | null;
      awayGoals?: number | null;
    }
  >;
  users?: Record<string, { id: string; displayName: string }>;
}

export interface TicketFormContext {
  teams: TeamDto[];
  matches: Array<{
    id: string;
    stage: MatchStage;
    round: number | null;
    scheduledAt: number | null;
    homeTeamId: string | null;
    awayTeamId: string | null;
    status: MatchStatus;
    tournamentName: string;
  }>;
}

export const ticketApi = {
  create: (body: {
    category: TicketCategory;
    subject: string;
    body: string;
    matchId?: string | null;
    teamId?: string | null;
    tournamentId?: string | null;
  }) => api<{ ticket: TicketDto }>('/tickets', { method: 'POST', body: JSON.stringify(body) }),
  mine: () => api<{ tickets: TicketDto[]; context: TicketContext }>('/tickets/mine'),
  formContext: () => api<TicketFormContext>('/tickets/context'),
};

/* ------------------------------ admin ------------------------------ */

export const adminApi = {
  artilhariaManual: (tournamentId: string) =>
    api<ManualStatsContext>(`/admin/tournaments/${tournamentId}/artilharia/manual`),
  somarArtilharia: (
    tournamentId: string,
    body: { teamId: string; playerId?: string | null; playerName?: string | null; goals: number; assists: number },
  ) =>
    api<{ entry: ManualStatsEntry }>(`/admin/tournaments/${tournamentId}/artilharia/manual`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  searchEaClubs: (clubName: string, platform = 'common-gen5') =>
    api<{ clubs: EaClubSearchResult[] }>(
      `/teams/ea-search?clubName=${encodeURIComponent(clubName)}&platform=${encodeURIComponent(platform)}`,
    ),
  /** Resolve um id exato — devolve no máximo um clube, ou nenhum. */
  buscarClubeEaPorId: (clubId: string, platform = 'common-gen5') =>
    api<{ clubs: EaClubSearchResult[] }>(
      `/teams/ea-search?clubId=${encodeURIComponent(clubId)}&platform=${encodeURIComponent(platform)}`,
    ),
  updateTeamEaClubId: (teamId: string, eaClubId: string) =>
    api<{ team: TeamDto }>(`/teams/${teamId}`, {
      method: 'PATCH',
      body: JSON.stringify({ eaClubId: eaClubId.trim() }),
    }),
  createTournament: (body: Record<string, unknown>) =>
    api<{ tournament: TournamentDetail }>('/admin/tournaments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTournament: (id: string, body: Record<string, unknown>) =>
    api<{ tournament: TournamentDetail }>(`/admin/tournaments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  allTournaments: () =>
    api<{ tournaments: TournamentDetail[] }>('/admin/tournaments'),
  /** Leitura completa da edição, com os dados de recebimento. */
  tournamentBySlug: (slug: string) =>
    api<{ tournament: TournamentDetail }>(`/admin/tournaments/by-slug/${slug}`),
  /** `refazer` é exigido pelo servidor quando a edição já foi sorteada. */
  refazerSorteio: (tournamentId: string, seed?: string) =>
    api<{ fixturesCreated: number }>(`/admin/tournaments/${tournamentId}/draw`, {
      method: 'POST',
      body: JSON.stringify({ refazer: true, ...(seed ? { seed } : {}) }),
    }),
  adicionarEquipe: (tournamentId: string, teamId: string) =>
    api<{ ok: true; confirmadas: number; capacidade: number; precisaRefazerSorteio: boolean }>(
      `/admin/tournaments/${tournamentId}/equipes`,
      { method: 'POST', body: JSON.stringify({ teamId }) },
    ),
  /**
   * Define na mão quem joga um confronto do mata-mata.
   *
   * `undefined` mantém o lado como está; `null` esvazia — sem essa distinção,
   * mexer num lado só apagaria o outro.
   */
  definirEquipesDaPartida: (
    matchId: string,
    equipes: { homeTeamId?: string | null; awayTeamId?: string | null },
  ) =>
    api<{ ok: true; homeTeamId: string | null; awayTeamId: string | null }>(
      `/admin/matches/${matchId}/equipes`,
      { method: 'PATCH', body: JSON.stringify(equipes) },
    ),
  /**
   * Gera o link que entrega um time ao capitão dele.
   *
   * O token só volta nesta resposta — no banco fica o hash. Se a pessoa
   * perder o link, o caminho é gerar outro, não recuperar este.
   */
  /**
   * Cadastro manual de usuário, com o papel definido na hora.
   *
   * Sem `password`, a conta nasce sem acesso e a resposta traz `link` — o
   * caminho para a pessoa definir a própria senha.
   */
  criarUsuario: (body: {
    email: string;
    displayName: string;
    role: string;
    password?: string | null;
  }) =>
    api<{
      user: { id: string; email: string; displayName: string; role: string };
      link: { caminho: string; expiraEm: number } | null;
    }>('/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Inscreve um clube na mão: `teamId` para clube existente, ou os dados para
   * criar na hora. O ID da EA é opcional aqui, ao contrário do cadastro pelo
   * site — dá para vincular depois.
   */
  inscreverClube: (
    tournamentId: string,
    body:
      | { teamId: string }
      | { name: string; shortName?: string; platform?: string; eaClubId?: string | null },
  ) =>
    api<{ team: TeamDto }>(`/admin/tournaments/${tournamentId}/inscricoes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  gerarLinkDeSenha: (userId: string) =>
    api<{ caminho: string; expiraEm: number }>(`/admin/users/${userId}/senha/link`, {
      method: 'POST',
    }),
  gerarConviteDeTime: (teamId: string) =>
    api<{ caminho: string; expiraEm: number }>(`/admin/teams/${teamId}/convite`, {
      method: 'POST',
    }),
  /**
   * Reordena as rodadas da fase de grupos.
   *
   * `ordem` é a lista das rodadas atuais na posição desejada: `[3, 1, 2]`
   * transforma a atual 3ª na 1ª.
   */
  reordenarRodadas: (tournamentId: string, ordem: number[]) =>
    api<{ ok: true; ordem: number[]; partidasAlteradas: number }>(
      `/admin/tournaments/${tournamentId}/rodadas`,
      { method: 'POST', body: JSON.stringify({ ordem }) },
    ),
  /**
   * Troca uma equipe por outra mantendo grupo, adversários e datas.
   *
   * Devolve quantas partidas mudaram de dono e quantas tiveram o placar
   * zerado — a substituta não herda o que a outra jogou.
   */
  substituirEquipe: (tournamentId: string, saindoId: string, substitutoId: string) =>
    api<{
      ok: true;
      partidasTransferidas: number;
      partidasZeradas: number;
      grupoTrocado: boolean;
    }>(`/admin/tournaments/${tournamentId}/equipes/${saindoId}/substituir`, {
      method: 'POST',
      body: JSON.stringify({ substitutoId }),
    }),
  removerEquipe: (tournamentId: string, teamId: string) =>
    api<{ ok: true; precisaRefazerSorteio: boolean }>(
      `/admin/tournaments/${tournamentId}/equipes/${teamId}`,
      { method: 'DELETE' },
    ),
  /** Devolve o confronto ao estado de agendado, apagando placar e súmula. */
  limparResultado: (matchId: string) =>
    api<{ match: MatchDto }>(`/admin/matches/${matchId}/resultado`, { method: 'DELETE' }),
  draw: (tournamentId: string, seed?: string) =>
    api<{ draw: { seed: string; groups: Array<{ key: string; teamIds: string[] }>; sequence: Array<{ teamId: string; groupKey: string; slot: number }> }; fixturesCreated: number }>(
      `/admin/tournaments/${tournamentId}/draw`,
      { method: 'POST', body: JSON.stringify(seed ? { seed } : {}) },
    ),
  deleteDraw: (tournamentId: string) =>
    api<{ ok: boolean; message: string }>(`/admin/tournaments/${tournamentId}/draw`, {
      method: 'DELETE',
    }),
  generateKnockout: (tournamentId: string) =>
    api<{ created: string[] }>(`/admin/tournaments/${tournamentId}/knockout`, { method: 'POST' }),
  declararCampeao: (tournamentId: string, teamId: string, motivo: string) =>
    api<{ ok: true; partidasCanceladas: number }>(`/admin/tournaments/${tournamentId}/campeao`, {
      method: 'POST',
      body: JSON.stringify({ teamId, motivo }),
    }),
  desfazerCampeao: (tournamentId: string) =>
    api<{ ok: true }>(`/admin/tournaments/${tournamentId}/campeao`, { method: 'DELETE' }),
  advanceKnockout: (tournamentId: string) =>
    api<{ advanced: string[]; champion: string | null }>(
      `/admin/tournaments/${tournamentId}/knockout/advance`,
      { method: 'POST' },
    ),
  updateMatch: (matchId: string, patch: Record<string, unknown>) =>
    api<{ match: MatchDto }>(`/admin/matches/${matchId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  walkover: (matchId: string, winner: 'home' | 'away', reason?: string) =>
    api<{ ok: true; score: string }>(`/admin/matches/${matchId}/walkover`, {
      method: 'POST',
      body: JSON.stringify({ winner, reason }),
    }),
  updateRegistration: (
    registrationId: string,
    status: 'confirmed' | 'rejected' | 'refunded' | 'awaiting_payment' | 'paid',
  ) =>
    api<{ ok: true; status: string }>(`/admin/registrations/${registrationId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  requestSync: (tournamentId: string) =>
    api<{ jobId: string; clubsQueued: number; message: string }>(
      `/admin/tournaments/${tournamentId}/sync`,
      { method: 'POST' },
    ),
  agents: () => api<{ agents: AgentDto[] }>('/admin/agents'),
  collectorStatus: () =>
    api<{ agents: number; lastSeenAt: number | null; online: boolean; queued: number }>(
      '/admin/collector-status',
    ),
  createAgent: (name: string) =>
    api<{ agent: { id: string; name: string }; token: string; warning: string }>('/admin/agents', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  revokeAgent: (id: string) =>
    api<{ agent: { id: string; name: string; revokedAt: number } }>(`/admin/agents/${id}`, {
      method: 'DELETE',
    }),
  tickets: (status?: string) =>
    api<{ tickets: TicketDto[]; open: number; context: TicketContext }>(
      `/admin/tickets${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  resolveTicket: (
    id: string,
    body: {
      status?: TicketStatus;
      resolution?: string;
      sanction?: {
        kind: 'warning' | 'admin_loss' | 'elimination' | 'ban' | 'suspension';
        teamId: string;
        reason: string;
        applyWalkover?: boolean;
      } | null;
    },
  ) =>
    api<{ ticket: TicketDto; sanctionId: string | null; walkoverApplied: string | null }>(
      `/admin/tickets/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  syncJob: (jobId: string) =>
    api<{
      job: {
        id: string;
        status: 'queued' | 'claimed' | 'running' | 'succeeded' | 'failed' | 'expired';
        error: string | null;
        createdAt: number;
        finishedAt: number | null;
        summary: {
          clubsFetched: number;
          matchesReceived: number;
          matchesLinked: number;
          playersUpserted: number;
          /** Confrontos que receberam resultado — o painel revela essas linhas. */
          linkedMatchIds?: string[];
        } | null;
      };
    }>(`/admin/sync-jobs/${jobId}`),
};

export interface ManualStatsEntry {
  id: string;
  tournamentId: string;
  teamId: string;
  playerId: string | null;
  playerKey: string;
  playerName: string;
  goals: number;
  assists: number;
  createdAt: number;
  updatedAt: number;
}

export interface ManualStatsContext {
  teams: Array<{
    id: string;
    name: string;
    shortName: string;
    players: Array<{
      id: string;
      teamId: string;
      displayName: string;
      eaPlayerName: string | null;
    }>;
  }>;
  entries: ManualStatsEntry[];
}
